import {useActiveShiftFocus} from "../../hooks/useActiveShiftFocus";
import {clearActiveShiftFocus, readActiveShiftFocus} from "../../hooks/active-shift-navigation";
import {
  addDays,
  adjustToTimeZone,
  DispatchEntity,
  dispatchesSelector,
  endOfDay,
  firstOrUndefined,
  formatISO,
  getObjectId,
  PersonEntity,
  personsSelector,
  personType,
  shiftsSelector,
  startOfDay,
  subDays,
  useDispatchPrefetch,
  useGetAllPersonsQuery,
  useGetDispatchesByDayQuery,
  useSearchShiftsQuery,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {DispatchWorkspace, EntityCommentDialog, type EntityCommentTarget} from "@ots-frontend/ots-ui-tw";
import {useDispatch} from "react-redux";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {getAppDttm} from "../../store/ui/selectors";
import {setAppDate} from "../../store/ui/actions";
import {useLocation, useNavigate} from "react-router-dom";
import {LoadingOverlay, Text} from "@mantine/core";
import {
  CommentModal,
  DispatchBatchNotifyModal,
  DispatchBlockDataGrid,
  DispatchDataGrid,
  DispatchDataGridContextMenuItem,
  DispatchFilterFormModal,
  DispatchFilterFormValues,
  DispatchNotifyModal,
  DispatchSeparatorMode,
  DriverSelectModal,
  getDispatchSeparatorDisplayValue,
  getDispatchSeparatorValue,
  GridSort,
  useAuth,
  isDefaultDispatchFilter,
  ResizeableContent,
  ThemedPage,
  useApp,
  useDispatchMutation,
} from "@ots-frontend/ots-ui";
import {skipToken} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {getDispatchPageFilter, getDispatchPageState} from "../../store/dispatch-page/selectors";
import {useDisclosure} from "@mantine/hooks";
import {CompactSelection, DataEditorRef, GridSelection, Item} from "@glideapps/glide-data-grid";
import {
  setDispatchPageFilter,
  setDispatchPageStateCell,
  setDispatchPageStateSeparatorMode,
  setDispatchPageStateSort,
  setDispatchPageStateView,
  setDispatchPageStateWidths,
} from "../../store/dispatch-page/actions";
import {CellClickedEventArgs} from "@glideapps/glide-data-grid/dist/ts/data-grid/data-grid-types";
import {DispatchDataGridView} from "../../store/dispatch-page/dispatch-page-store";

export type DispatchPageProps = Record<string, never>;

const compareNumbers = (a: number | undefined, b: number | undefined): number => {
  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }
  if (typeof a === "number") {
    return -1;
  }
  if (typeof b === "number") {
    return 1;
  }
  return 0;
};

const compareStrings = (a: string | undefined, b: string | undefined): number => {
  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b);
  }
  if (typeof a === "string") {
    return -1;
  }
  if (typeof b === "string") {
    return 1;
  }
  return 0;
};

const compareDispatches = (a: DispatchEntity, b: DispatchEntity, separatorMode: DispatchSeparatorMode): number => {
  const separatorCompare =
    separatorMode === "SCHEDULED_6_HOUR"
      ? compareStrings(getDispatchSeparatorValue(a, separatorMode), getDispatchSeparatorValue(b, separatorMode))
      : compareStrings(
          getDispatchSeparatorDisplayValue(a, separatorMode),
          getDispatchSeparatorDisplayValue(b, separatorMode)
        );

  if (separatorCompare !== 0) {
    return separatorCompare;
  }

  if ((a.personId ?? "") === (b.personId ?? "")) {
    const performOrderCompare = compareNumbers(a.performOrder, b.performOrder);
    if (performOrderCompare !== 0) {
      return performOrderCompare;
    }
  }

  return compareNumbers(a.scheduleDttm, b.scheduleDttm) || a.id.localeCompare(b.id);
};

export const DispatchPage = () => {
  const {appCategory} = useApp();
  const appDttm = useAppSelector(getAppDttm);
  const filter = useAppSelector(getDispatchPageFilter);
  const gridState = useAppSelector(getDispatchPageState);

  const dataEditorRef = useRef<DataEditorRef>(null);
  const dataGridRef = useRef<HTMLDivElement>(null);
  const [gridControlsTarget, setGridControlsTarget] = useState<HTMLDivElement | null>(null);
  const [commentTarget, setCommentTarget] = useState<EntityCommentTarget>();
  const {hasAccess} = useAuth();

  const [searchActive, setSearchActive] = useState(false);
  const onSearchClose = useCallback(() => setSearchActive(false), []);

  const [selection, setSelection] = useState<GridSelection>({
    columns: CompactSelection.empty(),
    rows: CompactSelection.empty(),
  });

  const [colWidths, setColWidths] = useState<(number | undefined)[]>(gridState.widths);
  const [sort, setSort] = useState<GridSort | undefined>(gridState.sort);

  const [filterOpened, filterHandler] = useDisclosure(false);
  const {t} = useTranslation(["translation", "common"]);
  const {regionId} = useApp();
  const navigate = useNavigate();
  const {pathname, search, state} = useLocation();
  const dispatch = useDispatch();

  const [selectedDispatches, setSelectedDispatches] = useState<DispatchEntity[]>([]);

  const driverModalDisclosure = useDisclosure(false);
  const driverModalOpened = driverModalDisclosure[0];
  const driverModalHandlers = driverModalDisclosure[1];

  const cancelDisclosure = useDisclosure(false);

  const notifyDisclosure = useDisclosure(false);

  const prefetchDispatches = useDispatchPrefetch("getDispatchesByDay");

  const view: DispatchDataGridView = useMemo(() => {
    if (readActiveShiftFocus(search, regionId).personId) return "LIST";
    const _view = state?.view ?? gridState?.view ?? "LIST";
    if (_view === "LIST" || _view === "BLOCK") {
      return _view;
    }
    return "LIST";
  }, [state?.view, gridState?.view, search, regionId]);

  const selectDrivers = useMemo(() => {
    return personsSelector();
  }, []);

  const {drivers} = useGetAllPersonsQuery(regionId ?? skipToken, {
    selectFromResult: (result) => ({
      ...result,
      drivers: selectDrivers(result, {
        id: getObjectId(filter.person),
        regionId: regionId,
        personType: personType.driver,
        showDoNotUse: false,
        active: true,
      }),
      isFetching: result.isFetching,
    }),
  });

  const selectDispatches = useMemo(() => {
    return dispatchesSelector();
  }, []);

  const selectShiftEntities = useMemo(() => {
    return shiftsSelector();
  }, []);

  const queryArg = useMemo(() => {
    if (regionId) {
      return {
        day: formatISO(startOfDay(appDttm)),
        regionId: regionId,
      };
    } else {
      return skipToken;
    }
  }, [appDttm, regionId]);

  const {dispatches, metadata, isFetching, refetch} = useGetDispatchesByDayQuery(queryArg, {
    pollingInterval: 120000,
    selectFromResult: (result) => ({
      ...result,
      ...selectDispatches(result, {
        regionId: regionId,
        category: appCategory,
        personId: getObjectId(filter.person),
        vehicleId: getObjectId(filter.vehicle),
        trailerId: getObjectId(filter.trailer),
        pickupSiteId: getObjectId(filter.pickupSite),
        pickupSiteCompanyId: getObjectId(filter.pickupSiteCompany),
        deliverySiteId: getObjectId(filter.deliverySite),
        deliverySiteCompanyId: getObjectId(filter.deliverySiteCompany),
        includeUnassigned: view === "BLOCK" ? true : undefined,
      }),
      isFetching: result.isFetching,
    }),
  });

  const driverFocus = useActiveShiftFocus(regionId, dataEditorRef, isFetching, true);

  const shiftQueryArg = useMemo(() => {
    if (regionId) {
      return {
        fromDttm: adjustToTimeZone(subDays(startOfDay(appDttm), 1)).getTime(),
        toDttm: adjustToTimeZone(addDays(endOfDay(appDttm), 1)).getTime(),
        regionIds: [regionId],
      };
    }
    return skipToken;
  }, [appDttm, regionId]);

  const {shifts, refetch: refetchShifts} = useSearchShiftsQuery(shiftQueryArg, {
    pollingInterval: 120000,
    selectFromResult: (result) => ({
      ...result,
      shifts: selectShiftEntities(result, {
        dateSort: true,
      }),
    }),
  });

  const sortedDispatches = useMemo(() => {
    return [...dispatches].sort((a, b) => compareDispatches(a, b, gridState.separatorMode ?? "DRIVER"));
  }, [dispatches, gridState.separatorMode]);

  const prefetchDttmRange = useCallback(
    (date: Date) => {
      const rootDate = startOfDay(date);

      // prefetch the previous day
      if (regionId) {
        prefetchDispatches({
          day: formatISO(subDays(rootDate, 1)),
          regionId: regionId,
        });

        // prefetch the next day
        prefetchDispatches({
          day: formatISO(addDays(rootDate, 1)),
          regionId: regionId,
        });
      }
    },
    [prefetchDispatches, regionId]
  );

  const {updateDispatches, patchDispatch, deleteDispatch, cancelDispatch, isUpdating} = useDispatchMutation();

  const onDateChange = useCallback(
    (date: Date) => {
      dispatch(setAppDate(date.getTime()));
      prefetchDttmRange(date);
    },
    [dispatch, prefetchDttmRange]
  );

  const onItemClick = useCallback(
    (item: DispatchDataGridContextMenuItem, data: DispatchEntity[]) => {
      if (item === "COMMENT" && data.length === 1 && hasAccess("dispatch_edit")) {
        setCommentTarget({
          objectId: data[0].id,
          objectType: "DISPATCH",
          label: `dispatch ${data[0].workOrderNumber ?? data[0].id}`,
        });
      } else if (item === "NEW") {
        navigate("form", {
          state: {
            id: data[0].id,
            copy: true,
          },
        });
      } else if (item === "EDIT") {
        navigate("form", {state: {id: data[0].id}});
      } else if (item === "DELETE") {
        deleteDispatch(data[0].id, undefined);
      } else if (item === "ASSIGN") {
        setSelectedDispatches(data);
        driverModalHandlers.open();
      } else if (item === "CANCEL") {
        setSelectedDispatches(data);
        cancelDisclosure[1].open();
      } else if (item === "NOTIFY") {
        setSelectedDispatches(data);
        notifyDisclosure[1].open();
      } else if (item === "CLEAR") {
        setSelectedDispatches([]);
      }
    },
    [navigate, deleteDispatch, driverModalHandlers, cancelDisclosure, notifyDisclosure, hasAccess]
  );

  const onCancelSubmit = useCallback(
    (comments: string | undefined) => {
      if (comments && selectedDispatches.length > 0) {
        const dispatch = selectedDispatches[0];
        cancelDispatch(dispatch.asTypeValue(), comments, undefined);
        cancelDisclosure[1].close();
      }
    },
    [selectedDispatches, cancelDispatch, cancelDisclosure]
  );

  const initialGridCell = useRef(gridState?.cell);
  const restoreInitialCell = useRef(true);
  useEffect(() => {
    const cell = initialGridCell.current;
    if (driverFocus.personId) {
      restoreInitialCell.current = false;
      return;
    }
    if (!restoreInitialCell.current || !cell) return;
    const timer = window.setTimeout(() => {
      restoreInitialCell.current = false;
      dataEditorRef.current?.scrollTo(0, cell[1], "both", 0, 0, {vAlign: "start", hAlign: "start"});
      setSelection((previous) => ({
        ...previous,
        current: {
          cell: [0, cell[1]],
          range: {x: 0, y: cell[1], width: 1, height: 1},
          rangeStack: [],
        },
      }));
    }, 200);
    return () => window.clearTimeout(timer);
  }, [driverFocus.personId]);

  const onFilterSubmit = useCallback(
    (filter: DispatchFilterFormValues) => {
      dispatch(setDispatchPageFilter(filter));
      filterHandler.close();
    },
    [dispatch, filterHandler]
  );

  const onCellClicked = useCallback(
    (cell: Item, event: CellClickedEventArgs) => {
      dispatch(setDispatchPageStateCell([cell[0], cell[1]]));
    },
    [dispatch]
  );

  const onViewClicked = useCallback(
    (view: DispatchDataGridView) => {
      navigate(pathname + (driverFocus.personId ? clearActiveShiftFocus(search) : search), {
        replace: true,
        state: {...state, view: view},
      });
      dispatch(setDispatchPageStateView(view));
    },
    [dispatch, navigate, pathname, search, state, driverFocus.personId]
  );

  const onSeparatorModeChange = useCallback(
    (mode: DispatchSeparatorMode) => {
      dispatch(setDispatchPageStateSeparatorMode(mode));
    },
    [dispatch]
  );

  useEffect(() => {
    return () => {
      dispatch(setDispatchPageStateSort(sort));
    };
  }, [sort, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setDispatchPageStateWidths(colWidths));
    };
  }, [colWidths, dispatch]);

  const onAssignDriver = useCallback(
    (persons: PersonEntity[] | undefined) => {
      const person = firstOrUndefined(persons);
      if (person) {
        const dispatches = [...selectedDispatches];
        dispatches.forEach((d) => (d.personId = person.id));

        const payloads = dispatches.map((d) => d.asTypeValue());
        updateDispatches(payloads, undefined);
      }
      driverModalHandlers.close();
      setSelection({
        columns: CompactSelection.empty(),
        rows: CompactSelection.empty(),
      });
    },
    [driverModalHandlers, selectedDispatches, updateDispatches]
  );

  return (
    <ThemedPage>
      {() => (
        <ResizeableContent>
          {(size) => (
            <div style={{height: size.height, width: size.width, minWidth: 0}}>
              <DispatchWorkspace
                date={new Date(appDttm)}
                onDateChange={onDateChange}
                dispatchCount={dispatches.length}
                canceledRequestCount={metadata.canceledRequests}
                view={view}
                onViewChange={onViewClicked}
                showSearch={searchActive}
                onSearchClick={() => setSearchActive((value) => !value)}
                filterActive={!isDefaultDispatchFilter(filter)}
                onFilterClick={filterHandler.open}
                isRefreshing={isFetching}
                onRefresh={() => {
                  refetch();
                  refetchShifts();
                }}
                onNewDispatch={hasAccess("dispatch_create") ? () => navigate("form") : undefined}
                gridControlsRef={setGridControlsTarget}
                context={driverFocus.notice}
              >
                <LoadingOverlay visible={isFetching} overlayBlur={2} />
                {view === "LIST" && (
                  <DispatchDataGrid
                    focusPersonId={driverFocus.personId}
                    onFocusResolved={driverFocus.onResolved}
                    onCommentCreated={refetch}
                    controlsTarget={gridControlsTarget}
                    onSeparatorModeChange={onSeparatorModeChange}
                    ref={dataEditorRef}
                    height="100%"
                    showSearch={searchActive}
                    onSearchClose={onSearchClose}
                    onCellClicked={onCellClicked}
                    gridSelection={selection}
                    onGridSelectionChange={setSelection}
                    colWidths={gridState.widths}
                    onColumnResize={setColWidths}
                    sort={gridState.sort}
                    onSort={setSort}
                    data={sortedDispatches}
                    updateDispatches={updateDispatches}
                    patchDispatch={patchDispatch}
                    separatorMode={gridState.separatorMode ?? "DRIVER"}
                    shifts={shifts}
                    appDttm={appDttm}
                    onItemClick={onItemClick}
                  />
                )}

                {view === "BLOCK" && (
                  <div style={{height: "100%"}}>
                    <DispatchBlockDataGrid
                      ref={dataGridRef}
                      data={dispatches}
                      persons={drivers}
                      shifts={shifts}
                      onItemClick={onItemClick}
                      patchDispatch={patchDispatch}
                      appDttm={appDttm}
                    />
                  </div>
                )}
              </DispatchWorkspace>

              <DispatchFilterFormModal
                title={t<string>("common.action.filter", "Filter", {ns: "common"})}
                opened={filterOpened}
                onClose={filterHandler.close}
                onSubmit={onFilterSubmit}
                defaultValues={{
                  ...filter,
                }}
              />

              <DriverSelectModal
                select={{
                  withinPortal: true,
                }}
                title={t<string>("common.action.assign.driver", "Assign Driver")}
                size="lg"
                opened={driverModalOpened}
                onClose={driverModalHandlers.close}
                onSubmit={onAssignDriver}
              />

              <CommentModal
                title={t<string>("common.action.cancel", "Cancel")}
                opened={cancelDisclosure[0]}
                onClose={cancelDisclosure[1].close}
                onSubmit={onCancelSubmit}
                loading={isUpdating}
                color="red"
                message={
                  <Text size="sm">
                    {t(
                      "common.profile.entity.dispatch.action.cancel.alert.message",
                      "Are you sure you want to cancel this dispatch?"
                    )}
                  </Text>
                }
              />

              {selectedDispatches.length > 0 && (
                <>
                  {selectedDispatches.length > 1 ? (
                    <DispatchBatchNotifyModal
                      dispatchIds={selectedDispatches.map((d) => d.id)}
                      title={t<string>("common.action.notify", "Notify")}
                      size="lg"
                      opened={notifyDisclosure[0]}
                      onClose={notifyDisclosure[1].close}
                    />
                  ) : (
                    <DispatchNotifyModal
                      dispatchId={selectedDispatches[0].id}
                      title={t<string>("common.action.notify", "Notify")}
                      size="lg"
                      opened={notifyDisclosure[0]}
                      onClose={notifyDisclosure[1].close}
                    />
                  )}
                </>
              )}

              {commentTarget && (
                <EntityCommentDialog
                  target={commentTarget}
                  onCreated={refetch}
                  onClose={() => setCommentTarget(undefined)}
                />
              )}
            </div>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
